/**
 * UNIFIED AUTHENTICATION CONTEXT
 * 
 * This context consolidates authentication from two sources:
 * 1. Auth0 - Traditional OAuth authentication for regular login/signup
 * 2. Custom JWT - Seamless post-purchase authentication system
 * 
 * AUTHENTICATION FLOW AFTER PURCHASE:
 * 1. User completes Stripe payment
 * 2. Backend generates custom JWT token with 15-minute expiry
 * 3. Frontend receives auto-login URL with JWT in query params
 * 4. Frontend extracts JWT, validates it, and sets up auth state
 * 5. User is immediately logged in without Auth0 interaction
 * 
 * JWT STRUCTURE:
 * - userId: Internal user UUID
 * - email: User's email address
 * - auth0UserId: Corresponding Auth0 user ID (for data consistency)
 * - purpose: 'auto_login_after_purchase'
 * - iat: Issued at timestamp
 * - exp: Expires in 15 minutes (security)
 * 
 * UNIFIED API TOKEN SYSTEM:
 * - For Auth0 users: Uses Auth0 access token
 * - For JWT users: Uses custom JWT token
 * - Backend validateToken middleware handles both token types
 * - Frontend automatically includes correct token in API requests
 */

import React, { createContext, useContext, useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useAuth0 } from '@auth0/auth0-react';
import { emit } from '../utils/eventBus';
import {
  safeGetStorageItem,
  safeRemoveStorageItem,
  setCriticalStorageItem,
} from '../utils/browserStorage';
import { getAuth0RedirectOrigin } from '../utils/auth0Origin';
import fetchWithTimeout from '../utils/fetchWithTimeout';
import {
  clearHighRiskStepUpActionId,
  readHighRiskStepUpActionId,
} from '../utils/highRiskStepUpStorage';
import { resetAnalyticsIdentity } from '../utils/analytics';

const UnifiedAuthContext = createContext();
const AUTH0_SESSION_EXCHANGE_TIMEOUT_MS = 15000;

const embeddedAuthEndpoint = (path) => {
  const apiBase = process.env.REACT_APP_API_URL?.replace(/\/$/, '');
  return `${apiBase || ''}/auth/embedded/${path}`;
};

export const useUnifiedAuth = () => {
  const context = useContext(UnifiedAuthContext);
  if (!context) {
    throw new Error('useUnifiedAuth must be used within a UnifiedAuthProvider');
  }
  return context;
};

export const UnifiedAuthProvider = ({ children }) => {
  // Auth0 authentication state
  const { 
    user: auth0User, 
    isAuthenticated: auth0IsAuthenticated, 
    isLoading: auth0IsLoading,
    getAccessTokenSilently,
    getIdTokenClaims,
    loginWithRedirect: auth0LoginWithRedirect,
    logout: auth0Logout
  } = useAuth0();

  const [auth0StartupTimedOut, setAuth0StartupTimedOut] = useState(false);

  // Custom JWT authentication state
  const [customJwtState, setCustomJwtState] = useState({
    user: null,
    token: null,
    isAuthenticated: false,
    isLoading: false
  });
  const [hasCheckedStoredCustomJwt, setHasCheckedStoredCustomJwt] = useState(false);
  const activeCustomJwtTokenRef = useRef(null);
  const handledUrlCustomJwtTokenRef = useRef(null);
  const customJwtIsAuthenticatedRef = useRef(false);
  const auth0SessionExchangeKeyRef = useRef(null);
  const logoutInProgressRef = useRef(false);
  const [auth0SessionExchangeState, setAuth0SessionExchangeState] = useState({
    isLoading: false,
    error: null
  });

  useEffect(() => {
    customJwtIsAuthenticatedRef.current = customJwtState.isAuthenticated;
  }, [customJwtState.isAuthenticated]);

  // Unified authentication state
  const [unifiedAuth, setUnifiedAuth] = useState({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    authMethod: null, // 'auth0' | 'custom_jwt'
    token: null
  });

  useEffect(() => {
    if (!auth0IsLoading) {
      setAuth0StartupTimedOut(false);
      return undefined;
    }

    const timeoutId = setTimeout(() => {
      console.warn('[UnifiedAuth] Auth0 startup exceeded timeout; continuing without blocking app render');
      setAuth0StartupTimedOut(true);
    }, 8000);

    return () => clearTimeout(timeoutId);
  }, [auth0IsLoading]);

  /**
   * CUSTOM JWT TOKEN VALIDATION AND DECODING
   * Validates JWT structure and expiry, returns decoded payload or null
   */
  const validateCustomJwt = useCallback((token) => {
    try {
      if (!token || typeof token !== 'string') {
        console.log('[UnifiedAuth] Invalid token format');
        return null;
      }

      // Basic JWT structure validation (3 parts separated by dots)
      const parts = token.split('.');
      if (parts.length !== 3) {
        console.log('[UnifiedAuth] Invalid JWT structure');
        return null;
      }

      // Decode payload (middle part)
      const payload = JSON.parse(atob(parts[1]));
      
      // Validate required fields
      if (!payload.userId || !payload.email || !payload.purpose) {
        console.log('[UnifiedAuth] Missing required JWT fields');
        return null;
      }

      // Check if token is expired
      const now = Math.floor(Date.now() / 1000);
      if (payload.exp && payload.exp < now) {
        console.log('[UnifiedAuth] JWT token has expired');
        return null;
      }

      // Validate purpose (security check)
      // Accept both post-purchase tokens and lead auto-login tokens
      const validPurposes = ['auto_login_after_purchase', 'lead_auto_login', 'embedded_auth0_email'];
      if (!validPurposes.includes(payload.purpose)) {
        console.log('[UnifiedAuth] Invalid JWT purpose');
        return null;
      }

      console.debug('[UnifiedAuth] JWT validation successful');

      return payload;
    } catch (error) {
      console.error('[UnifiedAuth] JWT validation error:', error?.message || 'unknown error');
      return null;
    }
  }, []);

  /**
   * CUSTOM JWT AUTHENTICATION HANDLER
   * Processes custom JWT from URL parameters or localStorage
   */
  const handleCustomJwtAuth = useCallback(async (token) => {
    console.log('[UnifiedAuth] Processing custom JWT authentication');

    if (logoutInProgressRef.current) {
      console.debug('[UnifiedAuth] Ignoring custom JWT while logout is in progress');
      return false;
    }
    
    const payload = validateCustomJwt(token);
    if (!payload) {
      if (activeCustomJwtTokenRef.current === token) {
        activeCustomJwtTokenRef.current = null;
      }
      console.log('[UnifiedAuth] Custom JWT validation failed');
      return false;
    }

    if (activeCustomJwtTokenRef.current === token) {
      console.debug('[UnifiedAuth] Custom JWT already active; skipping duplicate state update');
      return true;
    }

    try {
      // Repeated SPA location changes can find the same stored token many times.
      // Treat one accepted token as idempotent so auth/payment routing effects do
      // not churn while checkout is trying to mount.
      activeCustomJwtTokenRef.current = token;

      // Store token for API requests. This is a critical auth write, so recover
      // from quota pressure caused by derived chart/indicator caches before
      // accepting the login as durable.
      const tokenStored = setCriticalStorageItem('localStorage', 'customAuthToken', token);
      if (!tokenStored) {
        throw new Error('Unable to persist BotSpot session token.');
      }
      
      // Set custom JWT authentication state
      setCustomJwtState({
        user: {
          id: payload.userId,
          email: payload.email,
          auth0Sub: payload.auth0UserId || null,
          // Add other user fields as needed
        },
        token: token,
        isAuthenticated: true,
        isLoading: false
      });

      console.log('[UnifiedAuth] Custom JWT authentication successful');
      return true;
    } catch (error) {
      if (activeCustomJwtTokenRef.current === token) {
        activeCustomJwtTokenRef.current = null;
      }
      console.error('[UnifiedAuth] Custom JWT authentication error:', error?.message || 'unknown error');
      return false;
    }
  }, [validateCustomJwt]);

  const getAuth0AccessToken = useCallback(async () => {
    const audience = process.env.REACT_APP_AUTH0_AUDIENCE;
    const tokenOptions = audience
      ? {
          authorizationParams: {
            audience,
            scope: 'openid profile email'
          }
        }
      : undefined;
    return getAccessTokenSilently(tokenOptions);
  }, [getAccessTokenSilently]);

  /**
   * TOKEN RETRIEVAL FOR API REQUESTS
   * Returns appropriate token based on current authentication method
   */
  const getApiToken = useCallback(async () => {
    console.debug('[UnifiedAuth] getApiToken called:', {
      authMethod: unifiedAuth.authMethod,
      hasCustomToken: !!customJwtState.token,
      isAuthenticated: unifiedAuth.isAuthenticated,
      auth0IsAuthenticated
    });

    if (customJwtState.token && (unifiedAuth.authMethod === 'custom_jwt' || customJwtState.isAuthenticated)) {
      console.debug('[UnifiedAuth] Returning custom JWT token');
      return customJwtState.token;
    }

    if (unifiedAuth.authMethod === 'custom_jwt') {
      if (!customJwtState.token) {
        console.error('[UnifiedAuth] CRITICAL: Custom JWT auth method but no token available!');
        console.debug('[UnifiedAuth] Attempting to retrieve from localStorage...');

        // Fallback: try to get token directly from localStorage
        const storedToken = safeGetStorageItem('localStorage', 'customAuthToken');
        if (storedToken && validateCustomJwt(storedToken)) {
          console.debug('[UnifiedAuth] Successfully retrieved token from localStorage');
          return storedToken;
        } else {
          console.error('[UnifiedAuth] No valid token in localStorage either');
          return null;
        }
      }
    } else if (unifiedAuth.authMethod === 'auth0' || auth0IsAuthenticated) {
      try {
        console.debug('[UnifiedAuth] Getting Auth0 access token...');
        const token = await getAuth0AccessToken();
        console.debug('[UnifiedAuth] Auth0 token retrieved successfully');
        return token;
      } catch (error) {
        console.error('[UnifiedAuth] Failed to get Auth0 access token:', error?.message || 'unknown error');
        return null;
      }
    }
    
    console.warn('[UnifiedAuth] No authentication method available, returning null');
    return null;
  }, [
    unifiedAuth.authMethod,
    unifiedAuth.isAuthenticated,
    customJwtState.token,
    customJwtState.isAuthenticated,
    auth0IsAuthenticated,
    getAuth0AccessToken,
    validateCustomJwt
  ]);

  const getFreshLoginIdToken = useCallback(async () => {
    if (!auth0IsAuthenticated || typeof getIdTokenClaims !== 'function') {
      return null;
    }
    try {
      const claims = await getIdTokenClaims();
      return claims?.__raw || null;
    } catch (error) {
      console.warn('[UnifiedAuth] Failed to get Auth0 ID token claims for fresh-login proof:', error?.message || 'unknown error');
      return null;
    }
  }, [auth0IsAuthenticated, getIdTokenClaims]);

  const loginWithRedirect = useCallback((options = {}) => {
    logoutInProgressRef.current = false;

    const authorizationParams = {
      ...(options?.authorizationParams || {})
    };

    // Normal login should let users switch accounts after logout instead of
    // silently reusing the browser's last social-provider session. Security
    // sensitive callers can still pass prompt: 'login' to force fresh auth.
    if (!authorizationParams.prompt) {
      authorizationParams.prompt = 'select_account';
    }

    return auth0LoginWithRedirect({
      ...options,
      authorizationParams
    });
  }, [auth0LoginWithRedirect]);

  /**
   * UNIFIED LOGOUT FUNCTION
   * Clears both Auth0 and custom JWT authentication
   */
  const logout = useCallback(async (options = {}) => {
    console.log('[UnifiedAuth] Logging out from unified auth system');
    resetAnalyticsIdentity();
    const hasAuth0SessionAtLogout = auth0IsAuthenticated;
    logoutInProgressRef.current = true;

    const tokenTimeout = new Promise((resolve) => {
      setTimeout(() => resolve(null), 1500);
    });
    
    try {
      // Call backend logout endpoint if we have a token
      const token = await Promise.race([getApiToken(), tokenTimeout]);
      if (token) {
        console.log('[UnifiedAuth] Calling backend logout endpoint');
        try {
          const response = await fetch(`${process.env.REACT_APP_API_URL}/auth/logout`, {
            method: 'POST',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            }
          });
          
          if (response.ok) {
            const result = await response.json();
            console.log('[UnifiedAuth] Backend logout successful:', result);
          } else {
            console.warn('[UnifiedAuth] Backend logout failed, but continuing with client-side logout');
          }
        } catch (backendError) {
          console.warn('[UnifiedAuth] Backend logout request failed:', backendError);
          // Continue with client-side logout even if backend fails
        }
      }
    } catch (tokenError) {
      console.log('[UnifiedAuth] No token available for backend logout');
    }
    
    // Immediately clear unified auth state to prevent any lingering authenticated state
    activeCustomJwtTokenRef.current = null;
    handledUrlCustomJwtTokenRef.current = null;
    auth0SessionExchangeKeyRef.current = null;
    setAuth0SessionExchangeState({ isLoading: false, error: null });
    setUnifiedAuth({
      user: null,
      isAuthenticated: false,
      isLoading: false,
      authMethod: null,
      token: null
    });
    
    // Clear custom JWT state
    setCustomJwtState({
      user: null,
      token: null,
      isAuthenticated: false,
      isLoading: false
    });
    
    // Clear stored custom token
    safeRemoveStorageItem('localStorage', 'customAuthToken');
    
    // Clear any other auth-related data from localStorage
    safeRemoveStorageItem('localStorage', 'auth0.token');
    safeRemoveStorageItem('localStorage', 'auth0.access_token');
    safeRemoveStorageItem('localStorage', 'auth0.id_token');
    
    // CRITICAL FIX: Clear course registration data on logout
    // This prevents registration data from persisting across different user sessions
    Object.keys(localStorage).forEach(key => {
      if (key.startsWith('ai-bootcamp-registered-') || 
          key.startsWith('edu-workshop-registered-') ||
          key.includes('course-registered-')) {
        console.log('[UnifiedAuth] Clearing course registration data:', key);
        safeRemoveStorageItem('localStorage', key);
      }
    });
    
    // Clear session storage as well
    sessionStorage.clear();
    
    console.log('[UnifiedAuth] All auth state and storage cleared');
    
    // Logout from Auth0 whenever its browser session is present. Auth0 social
    // logins are exchanged into BotSpot custom JWTs after login, so checking
    // only the active unified auth method would leave the Auth0 session alive
    // and recreate the BotSpot session immediately after local cleanup.
    if (hasAuth0SessionAtLogout) {
      console.log('[UnifiedAuth] Logging out via Auth0');
      const requestedLogoutParams = options?.logoutParams || {};
      try {
        auth0Logout({
          ...options,
          logoutParams: {
            returnTo: getAuth0RedirectOrigin(window.location),
            ...requestedLogoutParams
          }
        });
      } catch (auth0LogoutError) {
        console.warn('[UnifiedAuth] Auth0 logout failed after local cleanup; redirecting home', auth0LogoutError);
        window.location.href = '/';
      }
    } else {
      // For custom JWT or any other case, redirect to home page
      console.log('[UnifiedAuth] Redirecting to home page');
      // Use setTimeout to ensure state updates are processed before redirect
      setTimeout(() => {
        window.location.href = '/';
      }, 100);
    }
  }, [auth0Logout, auth0IsAuthenticated, getApiToken]);

  /**
   * AUTO-LOGIN TOKEN DETECTION
   *
   * Checks URL parameters for an autoLoginToken on every location change, not
   * just on provider mount. The provider lives above the Router so it can't
   * use useLocation; instead we patch history.pushState/replaceState to emit
   * a 'locationchange' event, and subscribe to that plus the native popstate.
   *
   * Why: the sales funnel SPA-navigates from /sales -> /sales-choose carrying
   * the autoLoginToken in the URL. Before this fix, the mount-only effect
   * never saw the token because the provider doesn't remount across SPA
   * transitions, leaving users unauthenticated at /sales-choose. A direct
   * handleCustomJwtAuth(token) call from the lead-submit handler also racing
   * with SalesPage's auth-redirect useEffect caused the user to get shoved
   * into /ai-agent (paywall) instead of /sales-choose, so we listen at the
   * URL level instead and stay out of the submit handler's way.
   */
  useEffect(() => {
    let cancelled = false;

    const checkUrlForAutoLoginToken = async () => {
      if (logoutInProgressRef.current) {
        console.debug('[UnifiedAuth] Skipping auto-login token check while logout is in progress');
        return;
      }

      const urlParams = new URLSearchParams(window.location.search);
      const autoLoginToken = urlParams.get('autoLoginToken');

      if (autoLoginToken) {
        if (handledUrlCustomJwtTokenRef.current === autoLoginToken) {
          return;
        }
        handledUrlCustomJwtTokenRef.current = autoLoginToken;
        console.log('[UnifiedAuth] Auto-login token detected in URL');

        // Remove token from URL for security (avoids leaking in referrer/history)
        const newUrl = new URL(window.location);
        newUrl.searchParams.delete('autoLoginToken');
        window.history.replaceState({}, '', newUrl);
        // BrowserRouter listens to popstate. Keep its location snapshot in sync
        // so later tab/query updates cannot reintroduce the removed JWT.
        window.dispatchEvent(new PopStateEvent('popstate'));

        await handleCustomJwtAuth(autoLoginToken);
      } else {
        const storedToken = safeGetStorageItem('localStorage', 'customAuthToken');
        if (storedToken && activeCustomJwtTokenRef.current !== storedToken) {
          console.log('[UnifiedAuth] Stored custom token found');
          await handleCustomJwtAuth(storedToken);
        }
      }
    };

    // Patch history methods once to emit a locationchange event on SPA nav.
    // Guarded with a window-level flag so React StrictMode double-mounts don't
    // double-patch and so other consumers (like the react-router internals)
    // keep working.
    if (typeof window !== 'undefined' && !window.__unifiedAuthLocationPatch) {
      window.__unifiedAuthLocationPatch = true;
      const originalPush = window.history.pushState;
      const originalReplace = window.history.replaceState;
      window.history.pushState = function (...args) {
        const result = originalPush.apply(this, args);
        window.dispatchEvent(new Event('locationchange'));
        return result;
      };
      window.history.replaceState = function (...args) {
        const result = originalReplace.apply(this, args);
        window.dispatchEvent(new Event('locationchange'));
        return result;
      };
    }

    // Initial check on mount. Keep auth loading until this finishes so a fresh
    // tab cannot redirect to /login before the saved BotSpot session is applied.
    checkUrlForAutoLoginToken().finally(() => {
      if (!cancelled) {
        setHasCheckedStoredCustomJwt(true);
      }
    });

    // Re-check on every location change (popstate = back/forward, locationchange = pushState/replaceState)
    const handler = () => {
      void checkUrlForAutoLoginToken();
    };
    window.addEventListener('popstate', handler);
    window.addEventListener('locationchange', handler);
    return () => {
      cancelled = true;
      window.removeEventListener('popstate', handler);
      window.removeEventListener('locationchange', handler);
    };
  }, [handleCustomJwtAuth]);

  useEffect(() => {
    const handleStorageChange = (event) => {
      if (event.key !== 'customAuthToken' || logoutInProgressRef.current) {
        return;
      }

      if (event.newValue) {
        void handleCustomJwtAuth(event.newValue);
        return;
      }

      activeCustomJwtTokenRef.current = null;
      handledUrlCustomJwtTokenRef.current = null;
      setCustomJwtState({
        user: null,
        token: null,
        isAuthenticated: false,
        isLoading: false
      });
    };

    window.addEventListener('storage', handleStorageChange);
    return () => {
      window.removeEventListener('storage', handleStorageChange);
    };
  }, [handleCustomJwtAuth]);

  /**
   * MEMOIZED AUTH0 USER OBJECT
   * Prevents unnecessary re-renders by maintaining stable object identity
   * Only creates a new object when actual user values change
   */
  const memoizedAuth0User = useMemo(() => {
    if (!auth0IsAuthenticated || !auth0User) return null;

    // Note: email_verified is a boolean indicating if email is verified, not the email itself
    const userEmail = auth0User.email || null;
    return {
      id: auth0User.sub,
      email: userEmail,
      name: auth0User.name,
      auth0Sub: auth0User.sub,
    };
  }, [auth0IsAuthenticated, auth0User?.sub, auth0User?.email, auth0User?.name]);

  useEffect(() => {
    if (!auth0IsAuthenticated) {
      auth0SessionExchangeKeyRef.current = null;
      setAuth0SessionExchangeState({ isLoading: false, error: null });
      return;
    }

    if (logoutInProgressRef.current) {
      return;
    }

    const highRiskStepUpActionId = readHighRiskStepUpActionId();

    if (
      !memoizedAuth0User ||
      (customJwtIsAuthenticatedRef.current && !highRiskStepUpActionId) ||
      typeof getIdTokenClaims !== 'function'
    ) {
      return;
    }

    const baseExchangeKey = memoizedAuth0User.auth0Sub || memoizedAuth0User.id;
    const exchangeKey = highRiskStepUpActionId
      ? `${baseExchangeKey}:high-risk-step-up:${highRiskStepUpActionId}`
      : baseExchangeKey;
    if (!exchangeKey || auth0SessionExchangeKeyRef.current === exchangeKey) {
      return;
    }

    let cancelled = false;
    auth0SessionExchangeKeyRef.current = exchangeKey;
    setAuth0SessionExchangeState({ isLoading: true, error: null });

    const exchangeAuth0Session = async () => {
      if (logoutInProgressRef.current) {
        return;
      }

      try {
        const claims = await getIdTokenClaims();
        const idToken = claims?.__raw;
        if (!idToken) {
          throw new Error('Auth0 ID token was unavailable.');
        }

        const response = await fetchWithTimeout(
          embeddedAuthEndpoint('auth0-session'),
          {
            method: 'POST',
            headers: {
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({
              idToken,
              ...(highRiskStepUpActionId ? { highRiskStepUpActionId } : {})
            })
          },
          AUTH0_SESSION_EXCHANGE_TIMEOUT_MS
        );

        const data = await response.json().catch(() => ({}));
        if (!response.ok || !data?.token) {
          throw new Error(data?.message || 'Auth0 session exchange failed.');
        }

        if (cancelled) {
          return;
        }

        if (logoutInProgressRef.current) {
          return;
        }

        const accepted = await handleCustomJwtAuth(data.token);
        if (!accepted) {
          throw new Error('BotSpot session token was rejected.');
        }
        if (highRiskStepUpActionId) {
          clearHighRiskStepUpActionId(highRiskStepUpActionId);
        }

        setAuth0SessionExchangeState({ isLoading: false, error: null });
      } catch (error) {
        if (cancelled) {
          return;
        }

        console.warn('[UnifiedAuth] Auth0 session exchange failed; using Auth0 fallback if available:', error);
        setAuth0SessionExchangeState({
          isLoading: false,
          error: error?.message || 'Auth0 session exchange failed.'
        });
      }
    };

    exchangeAuth0Session();

    return () => {
      cancelled = true;
    };
  }, [
    auth0IsAuthenticated,
    memoizedAuth0User,
    getIdTokenClaims,
    handleCustomJwtAuth
  ]);

  /**
   * UNIFIED STATE MANAGEMENT
   * Combines Auth0 and custom JWT states into unified state
   * Uses memoized user objects to prevent re-render cascades
   */
  useEffect(() => {
    let newUnifiedState;
    const effectiveAuth0IsLoading = auth0IsLoading && !auth0StartupTimedOut;
    const auth0ExchangeKey = memoizedAuth0User?.auth0Sub || memoizedAuth0User?.id || null;
    const pendingHighRiskStepUpActionId = auth0SessionExchangeState.error
      ? null
      : readHighRiskStepUpActionId();
    const auth0SessionExchangeInProgress =
      auth0SessionExchangeState.isLoading ||
      (
        auth0IsAuthenticated &&
        Boolean(pendingHighRiskStepUpActionId)
      ) ||
      (
        auth0IsAuthenticated &&
        Boolean(auth0ExchangeKey) &&
        typeof getIdTokenClaims === 'function' &&
        auth0SessionExchangeKeyRef.current === auth0ExchangeKey &&
        !auth0SessionExchangeState.error &&
        !customJwtState.isAuthenticated
      );

    // Keep logout terminal on the client until Auth0/custom-JWT redirect
    // finishes. Otherwise the still-live Auth0 SDK state can briefly rebuild
    // an authenticated app session while the browser is leaving the page.
    if (logoutInProgressRef.current) {
      newUnifiedState = {
        user: null,
        isAuthenticated: false,
        isLoading: false,
        authMethod: null,
        token: null
      };
    } else if (auth0SessionExchangeInProgress) {
      newUnifiedState = {
        user: memoizedAuth0User,
        isAuthenticated: false,
        isLoading: true,
        authMethod: null,
        token: null
      };
    } else if (customJwtState.isAuthenticated) {
      // Priority: Custom JWT over Auth0 (post-purchase flow)
      newUnifiedState = {
        user: customJwtState.user,
        isAuthenticated: true,
        isLoading: false,
        authMethod: 'custom_jwt',
        token: customJwtState.token
      };
    } else if (auth0IsAuthenticated && memoizedAuth0User) {
      console.debug('[UnifiedAuth] Auth0 user mapping available:', {
        emailVerified: auth0User?.email_verified
      });

      newUnifiedState = {
        user: memoizedAuth0User, // Use memoized user for stable identity
        isAuthenticated: true,
        isLoading: false,
        authMethod: 'auth0',
        token: null // Will be retrieved via getApiToken when needed
      };
    } else {
      newUnifiedState = {
        user: null,
        isAuthenticated: false,
        isLoading: effectiveAuth0IsLoading || customJwtState.isLoading || !hasCheckedStoredCustomJwt,
        authMethod: null,
        token: null
      };
    }

    setUnifiedAuth(newUnifiedState);

    console.debug('[UnifiedAuth] Unified state updated:', {
      isAuthenticated: newUnifiedState.isAuthenticated,
      authMethod: newUnifiedState.authMethod
    });
  }, [
    auth0IsAuthenticated,
    memoizedAuth0User,
    auth0IsLoading,
    auth0StartupTimedOut,
    customJwtState,
    hasCheckedStoredCustomJwt,
    auth0User,
    auth0SessionExchangeState.isLoading,
    auth0SessionExchangeState.error,
    getIdTokenClaims
  ]);

  /**
   * CUSTOM JWT TOKEN CLEANUP
   * Removes expired tokens from localStorage
   */
  useEffect(() => {
    const cleanupExpiredTokens = () => {
      const storedToken = safeGetStorageItem('localStorage', 'customAuthToken');
      if (storedToken && !validateCustomJwt(storedToken)) {
        console.log('[UnifiedAuth] Removing expired custom token');
        if (activeCustomJwtTokenRef.current === storedToken) {
          activeCustomJwtTokenRef.current = null;
        }
        handledUrlCustomJwtTokenRef.current = null;
        safeRemoveStorageItem('localStorage', 'customAuthToken');
        setCustomJwtState({
          user: null,
          token: null,
          isAuthenticated: false,
          isLoading: false
        });
      }
    };

    // Check for expired tokens on mount and every 5 minutes
    cleanupExpiredTokens();
    const interval = setInterval(cleanupExpiredTokens, 5 * 60 * 1000);
    
    return () => clearInterval(interval);
  }, [validateCustomJwt]);

  /**
   * SESSION HEALTH CHECKS (Auth0)
   * - On window 'online' or when tab becomes visible, attempt a silent token fetch
   * - If Auth0 indicates reauth is required (login_required/consent_required), emit a global event
   */
  useEffect(() => {
    if (unifiedAuth.authMethod !== 'auth0' || !auth0IsAuthenticated) return;

    const tryRefresh = async (source) => {
      try {
        await getAccessTokenSilently();
      } catch (err) {
        const msg = String(err?.message || err?.error || 'token_error');
        if (msg.includes('login_required') || msg.includes('consent_required')) {
          console.warn('[UnifiedAuth] Auth0 token requires reauth:', msg);
          try {
            emit('auth:reauth_required', { source, error: msg, at: Date.now() });
          } catch (_) {}
        }
      }
    };

    const onOnline = () => tryRefresh('online');
    const onVisibility = () => {
      if (document.visibilityState === 'visible') tryRefresh('visibilitychange');
    };

    window.addEventListener('online', onOnline);
    document.addEventListener('visibilitychange', onVisibility);
    return () => {
      window.removeEventListener('online', onOnline);
      document.removeEventListener('visibilitychange', onVisibility);
    };
  }, [unifiedAuth.authMethod, auth0IsAuthenticated, getAccessTokenSilently]);

  const contextValue = {
    // Unified state
    user: unifiedAuth.user,
    isAuthenticated: unifiedAuth.isAuthenticated,
    isLoading: unifiedAuth.isLoading,
    authMethod: unifiedAuth.authMethod,
    
    // API integration
    getApiToken,
    getFreshLoginIdToken,
    
    // Authentication actions
    loginWithRedirect, // Auth0 login
    logout,
    
    // Custom JWT utilities
    handleCustomJwtAuth,
    validateCustomJwt,
    
    // Raw states for debugging
    auth0State: { user: auth0User, isAuthenticated: auth0IsAuthenticated, isLoading: auth0IsLoading, startupTimedOut: auth0StartupTimedOut },
    customJwtState
  };

  return (
    <UnifiedAuthContext.Provider value={contextValue}>
      {children}
    </UnifiedAuthContext.Provider>
  );
};

export default UnifiedAuthProvider;
